import { useState, useCallback } from 'react';
import { apiClient } from '../lib/api';
import { useToast } from './useToast';
import {
  Campaign,
  CampaignDetail,
  CampaignStats,
  CampaignRecipient,
  CampaignListResponse,
  CampaignRecipientListResponse,
  CampaignEnrollResponse,
  CampaignStepConfig,
} from '../types/campaign';

interface UseCampaignsState {
  campaigns: Campaign[];
  currentCampaign: CampaignDetail | null;
  recipients: CampaignRecipient[];
  stats: CampaignStats | null;
  total: number;
  isLoading: boolean;
  isLoadingRecipients: boolean;
  error: string | null;
}

interface UseCampaignsReturn extends UseCampaignsState {
  // List operations
  listCampaigns: (params?: { status?: string; limit?: number; offset?: number }) => Promise<CampaignListResponse>;
  refreshCampaigns: () => Promise<void>;

  // Single campaign operations
  getCampaign: (id: string) => Promise<CampaignDetail | null>;
  createCampaign: (data: {
    name: string;
    description?: string;
    channel?: string;
    credential_id?: string;
    timezone?: string;
    send_window_start?: string;
    send_window_end?: string;
    send_days?: string[];
    daily_limit?: number;
    dataset_ids?: string[];
  }) => Promise<Campaign | null>;
  updateCampaign: (id: string, data: {
    name?: string;
    description?: string;
    status?: string;
    channel?: string;
    credential_id?: string;
    timezone?: string;
    send_window_start?: string;
    send_window_end?: string;
    send_days?: string[];
    daily_limit?: number;
  }) => Promise<Campaign | null>;
  deleteCampaign: (id: string) => Promise<boolean>;

  // Status operations
  activateCampaign: (id: string, steps?: CampaignStepConfig[]) => Promise<boolean>;
  pauseCampaign: (id: string) => Promise<boolean>;
  changeCampaignStatus: (id: string, newStatus: string) => Promise<boolean>;

  // Stats
  getCampaignStats: (id: string) => Promise<CampaignStats | null>;

  // Recipients
  listRecipients: (campaignId: string, params?: { status?: string; limit?: number; offset?: number }) => Promise<CampaignRecipientListResponse>;
  enrollFromDataset: (campaignId: string, leadTableId: string, filterCriteria?: Record<string, unknown>) => Promise<CampaignEnrollResponse | null>;
  enrollRecipients: (campaignId: string, leadRowIds: string[], metadata?: Record<string, unknown>) => Promise<CampaignEnrollResponse | null>;

  // Clear state
  clearCurrentCampaign: () => void;
  clearError: () => void;
}

export function useCampaigns(): UseCampaignsReturn {
  const { showToast } = useToast();

  const [state, setState] = useState<UseCampaignsState>({
    campaigns: [],
    currentCampaign: null,
    recipients: [],
    stats: null,
    total: 0,
    isLoading: false,
    isLoadingRecipients: false,
    error: null,
  });

  const setLoading = useCallback((isLoading: boolean) => {
    setState(prev => ({ ...prev, isLoading, error: isLoading ? null : prev.error }));
  }, []);

  const setError = useCallback((error: string | null) => {
    setState(prev => ({ ...prev, error, isLoading: false }));
  }, []);

  const listCampaigns = useCallback(async (params?: { status?: string; limit?: number; offset?: number }): Promise<CampaignListResponse> => {
    setLoading(true);
    try {
      const response = await apiClient.listCampaigns(params);
      setState(prev => ({
        ...prev,
        campaigns: response.campaigns || [],
        total: response.total || 0,
        isLoading: false,
        error: null,
      }));
      return response;
    } catch {
      // Silently fail for list - campaigns feature might not be set up
      setState(prev => ({
        ...prev,
        campaigns: [],
        total: 0,
        isLoading: false,
      }));
      return { campaigns: [], total: 0 };
    }
  }, [setLoading]);

  const refreshCampaigns = useCallback(async () => {
    await listCampaigns();
  }, [listCampaigns]);

  const getCampaign = useCallback(async (id: string): Promise<CampaignDetail | null> => {
    setLoading(true);
    try {
      const campaign = await apiClient.getCampaign(id);

      // Also fetch stats
      let stats: CampaignStats | null = null;
      try {
        stats = await apiClient.getCampaignStats(id);
      } catch {
        // Stats might not exist yet, that's okay
      }

      const campaignDetail: CampaignDetail = {
        ...campaign,
        stats,
      };

      setState(prev => ({
        ...prev,
        currentCampaign: campaignDetail,
        stats,
        isLoading: false,
      }));
      return campaignDetail;
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Failed to load campaign';
      setError(message);
      showToast(message, 'error');
      return null;
    }
  }, [setLoading, setError, showToast]);

  const createCampaign = useCallback(async (data: {
    name: string;
    description?: string;
    channel?: string;
    credential_id?: string;
    timezone?: string;
    send_window_start?: string;
    send_window_end?: string;
    send_days?: string[];
    daily_limit?: number;
    dataset_ids?: string[];
  }): Promise<Campaign | null> => {
    setLoading(true);
    try {
      const campaign = await apiClient.createCampaign(data);
      setState(prev => ({
        ...prev,
        campaigns: [campaign, ...prev.campaigns],
        isLoading: false,
      }));
      showToast('Campaign created successfully', 'success');
      return campaign;
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Failed to create campaign';
      setError(message);
      showToast(message, 'error');
      return null;
    }
  }, [setLoading, setError, showToast]);

  const updateCampaign = useCallback(async (id: string, data: {
    name?: string;
    description?: string;
    status?: string;
    channel?: string;
    credential_id?: string;
    timezone?: string;
    send_window_start?: string;
    send_window_end?: string;
    send_days?: string[];
    daily_limit?: number;
  }): Promise<Campaign | null> => {
    setLoading(true);
    try {
      const campaign = await apiClient.updateCampaign(id, data);
      setState(prev => ({
        ...prev,
        campaigns: prev.campaigns.map(c => c.id === id ? campaign : c),
        currentCampaign: prev.currentCampaign?.id === id
          ? { ...prev.currentCampaign, ...campaign }
          : prev.currentCampaign,
        isLoading: false,
      }));
      showToast('Campaign updated', 'success');
      return campaign;
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Failed to update campaign';
      setError(message);
      showToast(message, 'error');
      return null;
    }
  }, [setLoading, setError, showToast]);

  const deleteCampaign = useCallback(async (id: string): Promise<boolean> => {
    setLoading(true);
    try {
      await apiClient.deleteCampaign(id);
      setState(prev => ({
        ...prev,
        campaigns: prev.campaigns.filter(c => c.id !== id),
        currentCampaign: prev.currentCampaign?.id === id ? null : prev.currentCampaign,
        isLoading: false,
      }));
      showToast('Campaign deleted', 'success');
      return true;
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Failed to delete campaign';
      setError(message);
      showToast(message, 'error');
      return false;
    }
  }, [setLoading, setError, showToast]);

  const activateCampaign = useCallback(async (id: string, steps?: CampaignStepConfig[]): Promise<boolean> => {
    setLoading(true);
    try {
      await apiClient.activateCampaign(id, steps);
      // Refresh campaign to get updated status
      await getCampaign(id);
      showToast('Campaign activated', 'success');
      return true;
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Failed to activate campaign';
      setError(message);
      showToast(message, 'error');
      return false;
    }
  }, [setLoading, setError, showToast, getCampaign]);

  const pauseCampaign = useCallback(async (id: string): Promise<boolean> => {
    setLoading(true);
    try {
      await apiClient.pauseCampaign(id);
      // Refresh campaign to get updated status
      await getCampaign(id);
      showToast('Campaign paused', 'success');
      return true;
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Failed to pause campaign';
      setError(message);
      showToast(message, 'error');
      return false;
    }
  }, [setLoading, setError, showToast, getCampaign]);

  const changeCampaignStatus = useCallback(async (id: string, newStatus: string): Promise<boolean> => {
    setLoading(true);
    try {
      await apiClient.changeCampaignStatus(id, newStatus);
      // Refresh campaign to get updated status
      await getCampaign(id);
      showToast(`Campaign status changed to ${newStatus}`, 'success');
      return true;
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Failed to change campaign status';
      setError(message);
      showToast(message, 'error');
      return false;
    }
  }, [setLoading, setError, showToast, getCampaign]);

  const getCampaignStats = useCallback(async (id: string): Promise<CampaignStats | null> => {
    try {
      const stats = await apiClient.getCampaignStats(id);
      setState(prev => ({
        ...prev,
        stats,
      }));
      return stats;
    } catch (error) {
      console.error('Failed to load campaign stats:', error);
      showToast('Failed to load campaign stats', 'error');
      return null;
    }
  }, [showToast]);

  const listRecipients = useCallback(async (
    campaignId: string,
    params?: { status?: string; limit?: number; offset?: number }
  ): Promise<CampaignRecipientListResponse> => {
    setState(prev => ({ ...prev, isLoadingRecipients: true }));
    try {
      const response = await apiClient.listCampaignRecipients(campaignId, params);
      setState(prev => ({
        ...prev,
        recipients: response.recipients || [],
        isLoadingRecipients: false,
      }));
      return response;
    } catch (error) {
      console.error('Failed to load recipients:', error);
      showToast('Failed to load recipients', 'error');
      setState(prev => ({ ...prev, isLoadingRecipients: false }));
      return { recipients: [], total: 0 };
    }
  }, [showToast]);

  const enrollFromDataset = useCallback(async (
    campaignId: string,
    leadTableId: string,
    filterCriteria?: Record<string, unknown>
  ): Promise<CampaignEnrollResponse | null> => {
    setLoading(true);
    try {
      const response = await apiClient.enrollFromDataset(campaignId, leadTableId, { filterCriteria });
      setLoading(false);
      showToast(`Enrolled ${response.enrolled_count} recipients`, 'success');
      // Refresh recipients
      await listRecipients(campaignId);
      // Refresh campaign to get updated count
      await getCampaign(campaignId);
      return response;
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Failed to enroll recipients';
      setError(message);
      showToast(message, 'error');
      return null;
    }
  }, [setLoading, setError, showToast, listRecipients, getCampaign]);

  const enrollRecipients = useCallback(async (
    campaignId: string,
    leadRowIds: string[],
    metadata?: Record<string, unknown>
  ): Promise<CampaignEnrollResponse | null> => {
    setLoading(true);
    try {
      const response = await apiClient.enrollRecipients(campaignId, leadRowIds, metadata);
      setLoading(false);
      showToast(`Enrolled ${response.enrolled_count} recipients`, 'success');
      // Refresh recipients
      await listRecipients(campaignId);
      return response;
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Failed to enroll recipients';
      setError(message);
      showToast(message, 'error');
      return null;
    }
  }, [setLoading, setError, showToast, listRecipients]);

  const clearCurrentCampaign = useCallback(() => {
    setState(prev => ({
      ...prev,
      currentCampaign: null,
      recipients: [],
      stats: null,
    }));
  }, []);

  const clearError = useCallback(() => {
    setState(prev => ({ ...prev, error: null }));
  }, []);

  return {
    ...state,
    listCampaigns,
    refreshCampaigns,
    getCampaign,
    createCampaign,
    updateCampaign,
    deleteCampaign,
    activateCampaign,
    pauseCampaign,
    changeCampaignStatus,
    getCampaignStats,
    listRecipients,
    enrollFromDataset,
    enrollRecipients,
    clearCurrentCampaign,
    clearError,
  };
}
